/**
 * ViewportOverlay - Four Corners Text Overlay
 *
 * Displays DICOM information in the four corners of the viewport:
 * - Top-Left: Generated-reformat (MPR) badge — shown only on a pane that
 *   reslices another series' volume, so a generated image is never mistaken
 *   for scanner output.
 * - Top-Right: (Reserved for study info - not implemented yet)
 * - Bottom-Left: Series Description, Series/Image Number, Slice Thickness
 * - Bottom-Right: Zoom Factor, Window/Level values
 *
 * Text styling: White with black drop shadow for readability on any background.
 */

import React from "react";
import type { PlaneOrientation } from "../../viewport/types";
import { PLANE_COLORS_HEX } from "../../constants/planeColors";

export interface SeriesInfo {
  seriesDescription: string | null;
  seriesNumber: number | null;
  imageNumber: number | null;
  totalImages: number;
  sliceThickness: number | null;
  seriesUid?: string | null; // For debugging
}

export interface ViewportMetrics {
  zoomFactor: number;
  windowCenter: number;
  windowWidth: number;
}

export interface ViewportOverlayProps {
  seriesInfo: SeriesInfo;
  viewportMetrics: ViewportMetrics;
  /** Whether zoom mode is active (Z key toggle) */
  isZoomMode?: boolean;
  /**
   * Phase label shown in the top-right at lowest priority — appears only
   * when no higher-priority badge (e.g. zoom-mode) is occupying the slot.
   */
  phaseLabel?: string | null;
  /** Optional adornment rendered at the top of the bottom-left cluster
   *  (above the series description). */
  modeBadge?: React.ReactNode;
  /** Set when this pane renders a client-side reformat (MPR) rather than an
   *  acquired series — badged top-left so a generated image is never mistaken
   *  for scanner output. */
  reformatPlane?: PlaneOrientation | null;
}

/**
 * Format slice thickness with unit
 */
function formatSliceThickness(thickness: number | null): string {
  if (thickness === null || thickness === undefined) return "";
  return `${thickness.toFixed(1)} mm`;
}

/**
 * Format window/level values
 */
function formatWindowLevel(center: number, width: number): string {
  return `W: ${Math.round(width)} L: ${Math.round(center)}`;
}

/**
 * Format zoom as percentage
 */
function formatZoom(zoom: number): string {
  return `${Math.round(zoom * 100)}%`;
}

export const ViewportOverlay: React.FC<ViewportOverlayProps> = ({
  seriesInfo,
  viewportMetrics,
  isZoomMode = false,
  phaseLabel = null,
  modeBadge = null,
  reformatPlane = null,
}) => {
  const { seriesDescription, seriesNumber, imageNumber, totalImages, sliceThickness, seriesUid } =
    seriesInfo;
  const { zoomFactor, windowCenter, windowWidth } = viewportMetrics;

  // Common text style with drop shadow for readability
  const textStyle = "text-white text-xs font-mono drop-shadow-[1px_1px_2px_rgba(0,0,0,0.9)]";

  const reformatColor = reformatPlane
    ? PLANE_COLORS_HEX[reformatPlane.toLowerCase() as keyof typeof PLANE_COLORS_HEX]
    : undefined;

  return (
    <div className="absolute inset-0 pointer-events-none select-none p-2">
      {/* Top-Left: generated-reformat badge. Plane color matches the 3D legend
          and CompanionPlaneToggle (PLANE_COLORS_HEX is the SSOT). The overlay is
          pointer-events-none, so no tooltip is possible here; visible text stands alone. */}
      {reformatPlane && (
        <div
          data-testid="viewport-reformat-badge"
          className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-2 border border-border-strong text-[10px] font-semibold tracking-wide uppercase"
          style={{ color: reformatColor }}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: reformatColor }}
          />
          {reformatPlane} MPR
        </div>
      )}

      {/* Top-Right: highest-priority badge wins; phase label is the fallback. */}
      <div className="absolute top-2 right-2 text-right">
        {isZoomMode ? (
          <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-signal/80 text-white text-[10px] font-semibold tracking-wide uppercase">
            Zoom (Z)
          </div>
        ) : phaseLabel ? (
          <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-2 border border-border-strong text-fg-muted text-[10px] font-semibold tracking-wide uppercase">
            {phaseLabel}
          </div>
        ) : null}
      </div>

      {/* Bottom-Left: Series Info */}
      <div className={`absolute bottom-2 left-2 ${textStyle}`}>
        {modeBadge && <div className="mb-0.5">{modeBadge}</div>}
        {seriesDescription && (
          <div className="truncate max-w-[200px]" title={seriesDescription}>
            {seriesDescription}
          </div>
        )}
        {(seriesNumber !== null || imageNumber !== null) && (
          <div data-testid="viewport-slice-readout">
            {seriesNumber !== null && `Ser: ${seriesNumber} `}
            {imageNumber !== null ? `Img: ${imageNumber}/${totalImages}` : null}
          </div>
        )}
        {sliceThickness !== null && <div>Thickness: {formatSliceThickness(sliceThickness)}</div>}
        {/* Debug: Series UID suffix for verification */}
        {seriesUid && (
          <div className="text-[9px] text-fg-muted font-mono" title={seriesUid}>
            UID: ...{seriesUid.slice(-12)}
          </div>
        )}
      </div>

      {/* Bottom-Right: Viewport Metrics */}
      <div className={`absolute bottom-2 right-2 text-right ${textStyle}`}>
        <div>{formatZoom(zoomFactor)}</div>
        <div>{formatWindowLevel(windowCenter, windowWidth)}</div>
      </div>
    </div>
  );
};

export default ViewportOverlay;
